// From: https://en.wikipedia.org/wiki/Glossary_of_contract_bridge_terms#top
// Seat
// Position relative to the dealer: for example, dealer's LHO is said to be in second seat.
// LHO
// Left-hand opponent
//

// Bidder
// partner
// declarer

const Seat = require('./Seat');
const Card = require('./Card');

/** Class representing a Bridge Player */
class Player {
	/** @param {Seat} seat*/
	constructor(seat) {
		if ('string' == typeof seat) {
			// allow string constructor
			seat = new Seat(seat);
		}

		this.seat = seat;
		this._hand = [];
	}

	/**
	 * Calculate the number of points in the hand
	 * @return {int} High Card Points
	 */
	get highCardPoints() {
		return this._hand.reduce((acc, card) => acc + card.value, 0);
	}

	/**
	 * Get the partner of the player
	 * @return {String} the name of the player who is this players partner e.g. S, N, W, or E
	 */
	get partner() {
		return this.seat.partner;
	}

	/**
	 * The short name of the player's seat
	 * @return {String} the players name in short format one of `E, W, N, S`
	 */
	toString() {
		return this.seat.shortName;
	}

	/**
	 * The short name for this players position
	 * @return {String} e.g. `S,E,W or N`
	 */
	get name() {
		return this.seat.shortName;
	}

	/**
	 * The name for the pair this player belongs to
	 * @return {String} one of `EW` or `NS`
	 */
	get pair() {
		return this.seat.pair;
	}

	/**
	 * The name of the seat to the left of this player
	 * @return {String} one of `N,S,E,W`
	 */
	get left() {
		return this.seat.left;
	}

	/**
	 * @param {Card} card adds a card to players hand
	 */
	addCard(card) {
		this._hand.push(card);
	}

	/**
	 * clears all card from the players hand
	 */
	emptyHand() {
		this._hand = [];
	}

	/**
	 * @return {Card[]} a list of Cards in the players hand
	 */
	get hand() {
		return this._hand;
	}

	get handAsList() {
		return this._hand.map((card) => {
			return { name: card.name, state: card.state, dds: card.dds };
		});
	}

	/**
	 * Return the players hand in PBN deal format
	 *
	 * SPADES.HEARTS.DIAMONDS.CLUBS
	 *
	 * @return {String} a list of card values sorted by suit order (S, H, D, C), seperated by dots e.g. 234.TJQ.342.579A
	 */
	get handPBN() {
		if (this._hand.length == 0) {
			return '-';
		}

		let suitCards = {
			S: [],
			D: [],
			H: [],
			C: [],
		};

		for (let suit of ['S', 'H', 'D', 'C']) {
			suitCards[suit] = this._hand.filter((card) => card.suit.short == suit);
			// reverse rank order
			suitCards[suit].sort((a, b) => b.rank.rankOrder - a.rank.rankOrder); // i.e. A is first, 2 is last
			suitCards[suit] = suitCards[suit].map((card) => card.rank.rank).join('');
		}

		let handStr = `${suitCards['S']}.${suitCards['H']}.${suitCards['D']}.${suitCards['C']}`;

		return handStr;
	}

	get unplayed() {
		return this._hand.filter((card) => card.state == 'dealt');
	}

	arrangeHand() {
		this._hand.sort(Card.compareHandSort).reverse();
	}

	/**
	 * Given the game vulnerability, determine if this player is vulnerable
	 * @param  {String}  gameVulnerable from game.vulnerable
	 * @return {Boolean} true if All or this players pair is vulnerable
	 */
	isVulnerable(gameVulnerable) {
		return gameVulnerable == 'All' || this.pair == gameVulnerable;
	}
}

/**
 * @param  {string} seat
 * @return {Player}
 */
Player.fromString = (name) => {
	return new Player(new Seat(name));
};

module.exports = Player;
